import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  FiShare2, FiEye, FiClock, FiArrowLeft, FiMessageSquare,
  FiTwitter, FiFacebook, FiLinkedin, FiLink, FiMessageCircle
} from 'react-icons/fi';
import fallbackImage1 from '../assets/hero.webp';
import fallbackImage2 from '../assets/hero.webp';
import HeartbeatLoader from '../components/HeartbeatLoader';

const SingleBlog = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [views, setViews] = useState(0);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [showShareOptions, setShowShareOptions] = useState(false);

  const localFallbackImages = [fallbackImage1, fallbackImage2];

  const getBlogUrl = (blogSlug) => `/article/${blogSlug}`;

  const processRelatedPosts = (posts) => {
    const processedPosts = posts.map((p) => {
      const urlParts = p.url?.split('/');
      let urlSlug = urlParts[urlParts.length - 1];
      if (urlSlug.endsWith('.html')) {
        urlSlug = urlSlug.replace('.html', '');
      }

      const imageUrl = `https://www.pcds.co.in/cpm/imagesarticle/b${p.id}.png`;

      return {
        ...p,
        slug: urlSlug,
        imageUrl: imageUrl,
        fallbackImage: localFallbackImages[Math.floor(Math.random() * localFallbackImages.length)],
        date: p.datec ? new Date(p.datec).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : 'No date',
        title: p.title || 'Untitled Article',
        excerpt: p.body?.replace(/<[^>]*>?/gm, '').substring(0, 100) + '...' || 'Read more...',
        author: p.username || 'Unknown Author'
      };
    });

    return processedPosts;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchPost = async () => {
      try {
        const response = await fetch('https://www.pcds.co.in/blogapi.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uri: 'get', limit: 20, category: 'latest' })
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Expected an array of posts');

        const foundPost = data.find(p => {
          if (!p.url) return false;
          const urlParts = p.url.split('/');
          let urlSlug = urlParts[urlParts.length - 1];
          if (urlSlug.endsWith('.html')) {
            urlSlug = urlSlug.replace('.html', '');
          }
          return urlSlug === slug;
        });

        if (!foundPost) {
          navigate('/404', { replace: true });
          return;
        }

        // Generate SEO metadata
        const seoTitle = foundPost.title || 'Latest Article | PCDS Blog';
        const seoDescription = foundPost.body 
          ? foundPost.body.replace(/<[^>]+>/g, '').substring(0, 160) 
          : 'Read this interesting article on PCDS blog';
        const seoKeywords = foundPost.title 
          ? `${foundPost.title}, ${foundPost.title.split(' ').join(', ')}` 
          : 'agriculture, farming, crops, pcds';
        
        const postWithDetails = {
          ...foundPost,
          slug: slug,
          author: foundPost.username || 'Unknown Author',
          views: foundPost.viewcount || 0,
          imageUrl: `https://www.pcds.co.in/cpm/imagesarticle/b${foundPost.id}.png`,
          fallbackImage: localFallbackImages[Math.floor(Math.random() * localFallbackImages.length)],
          date: foundPost.datec ? new Date(foundPost.datec).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'No date',
          readTime: `${Math.ceil(foundPost.body?.length / 1000) || 5} min read`,
          seoTitle,
          seoDescription,
          seoKeywords,
          ogType: 'article',
          canonicalUrl: `${window.location.origin}${getBlogUrl(slug)}`,
          publishedTime: foundPost.datec || new Date().toISOString()
        };

        setPost(postWithDetails);
        setViews(postWithDetails.views);

        // Process related posts
        const related = data
          .filter(p => p.id !== foundPost.id)
          .slice(0, 3);

        const processedRelated = processRelatedPosts(related);
        setRelatedPosts(processedRelated);

        const storedComments = JSON.parse(localStorage.getItem(`comments-${slug}`)) || [];
        setComments(storedComments);
      } catch (error) {
        console.error('Error fetching post:', error);
        navigate('/blog', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, navigate]);

  const shareArticle = async (platform) => {
    if (!post) return;

    const shareUrl = `${window.location.origin}${getBlogUrl(post.slug)}`;
    const shareText = `Check out this article: ${post.title}`;

    try {
      switch (platform) {
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'linkedin':
          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
          break;
        case 'copy':
          await navigator.clipboard.writeText(shareUrl);
          alert('Link copied to clipboard!');
          break;
        case 'native':
          if (navigator.share) {
            await navigator.share({
              title: post.title,
              text: shareText,
              url: shareUrl
            });
          } else {
            setShowShareOptions(true);
          }
          break;
        default:
          setShowShareOptions(!showShareOptions);
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!comment.trim() || !name.trim()) return;

    const newComment = {
      id: Date.now(),
      name,
      email,
      text: comment,
      date: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    };

    const updatedComments = [...comments, newComment];
    setComments(updatedComments);
    localStorage.setItem(`comments-${slug}`, JSON.stringify(updatedComments));
    
    setComment('');
    setName('');
    setEmail('');
  };

  const formatContent = (html) => {
    if (!html) return null;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Style headings
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(heading => {
      heading.style.fontWeight = '700';
      heading.style.marginTop = '2rem';
      heading.style.marginBottom = '1rem';
      heading.style.lineHeight = '1.3';
      heading.style.color = '#1a365d';
      
      // Add specific styles based on heading level
      if (heading.tagName === 'H1') {
        heading.style.fontSize = '2rem';
        heading.style.borderBottom = '2px solid #e2e8f0';
        heading.style.paddingBottom = '0.5rem';
      } else if (heading.tagName === 'H2') {
        heading.style.fontSize = '1.75rem';
        heading.style.borderBottom = '1px solid #e2e8f0';
        heading.style.paddingBottom = '0.5rem';
      } else if (heading.tagName === 'H3') {
        heading.style.fontSize = '1.5rem';
      }
    });

    // Style paragraphs
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      p.style.marginBottom = '1.5rem';
      p.style.lineHeight = '1.8';
      p.style.fontSize = '1.125rem';
      p.style.color = '#4a5568';
    });

    // Style lists
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      list.style.marginBottom = '1.5rem';
      list.style.paddingLeft = '1.5rem';
      
      const items = list.querySelectorAll('li');
      items.forEach(item => {
        item.style.marginBottom = '0.5rem';
        item.style.lineHeight = '1.6';
      });
    });

    // Style strong/bold elements
    const strongElements = tempDiv.querySelectorAll('strong, b');
    strongElements.forEach(el => {
      el.style.fontWeight = '700';
      el.style.color = '#2d3748';
    });

    // Style em/italic elements
    const emElements = tempDiv.querySelectorAll('em, i');
    emElements.forEach(el => {
      el.style.fontStyle = 'italic';
    });

    // Style links
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      link.style.color = '#3182ce';
      link.style.textDecoration = 'underline';
      link.style.fontWeight = '500';
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    // Style blockquotes
    const blockquotes = tempDiv.querySelectorAll('blockquote');
    blockquotes.forEach(quote => {
      quote.style.borderLeft = '4px solid #e2e8f0';
      quote.style.paddingLeft = '1rem';
      quote.style.marginLeft = '0';
      quote.style.marginRight = '0';
      quote.style.marginTop = '1.5rem';
      quote.style.marginBottom = '1.5rem';
      quote.style.color = '#4a5568';
      quote.style.fontStyle = 'italic';
    });

    // Style images
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '0.5rem';
      img.style.marginTop = '1.5rem';
      img.style.marginBottom = '1.5rem';
    });

    return { __html: tempDiv.innerHTML };
  };

  if (loading) {
    return <HeartbeatLoader />; // Use the Loader component here
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h2>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or may have been removed.</p>
          <Link to="/blog" className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg inline-flex items-center">
            <FiArrowLeft className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Helmet>
        <title>{post.seoTitle}</title>
        <meta name="description" content={post.seoDescription} />
        <meta name="keywords" content={post.seoKeywords} />
        <link rel="canonical" href={post.canonicalUrl} />
        <meta property="og:type" content={post.ogType} />
        <meta property="og:url" content={post.canonicalUrl} />
        <meta property="og:title" content={post.seoTitle} />
        <meta property="og:description" content={post.seoDescription} />
        <meta property="og:image" content={post.imageUrl || post.fallbackImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="PCDS Blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seoTitle} />
        <meta name="twitter:description" content={post.seoDescription} />
        <meta name="twitter:image" content={post.imageUrl || post.fallbackImage} />
        <meta name="twitter:creator" content="@pcds" />
        <meta property="article:published_time" content={post.publishedTime} />
        <meta property="article:author" content={post.author} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 leading-tight">{post.title}</h1>

            <div className="flex flex-wrap items-center text-sm text-gray-500 gap-4 mb-6">
              <div className="flex items-center">
                <img
                  loading="lazy"
                  src={`https://ui-avatars.com/api/?name=${post.author.replace(' ', '+')}&background=random`}
                  alt={post.author}
                  className="w-8 h-8 rounded-full mr-2"
                />
                <span>By {post.author}</span>
              </div>
              <div className="flex items-center"><FiClock className="mr-1" />{post.date}</div>
              <div className="flex items-center"><FiEye className="mr-1" />{views.toLocaleString()} views</div>
              <div className="flex items-center">{post.readTime}</div>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              loading="lazy"
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-96 object-cover"
              onError={(e) => e.target.src = post.fallbackImage}
            />
          </div>

          <article 
            className="mb-12 max-w-none text-gray-800" 
            dangerouslySetInnerHTML={formatContent(post.body)}
            style={{ lineHeight: '1.8' }}
          />

          <div className="flex flex-wrap gap-4 mb-12">
            <button
              onClick={() => shareArticle('twitter')}
              className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-md hover:bg-blue-500 transition-colors"
            >
              <FiTwitter className="mr-2" />
              Share on Twitter
            </button>
            <button
              onClick={() => shareArticle('facebook')}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FiFacebook className="mr-2" />
              Share on Facebook
            </button>
            <button
              onClick={() => shareArticle('linkedin')}
              className="flex items-center px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
            >
              <FiLinkedin className="mr-2" />
              Share on LinkedIn
            </button>
            <button
              onClick={() => shareArticle('whatsapp')}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <FiMessageCircle className="mr-2" />
              Share on WhatsApp
            </button>
            <button
              onClick={() => shareArticle('copy')}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <FiLink className="mr-2" />
              Copy Link
            </button>
          </div>

          <div id="comments" className="mb-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Comments ({comments.length})</h3>
            
            {comments.length > 0 ? (
              <div className="space-y-6 mb-8">
                {comments.map(c => (
                  <div key={c.id} className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-800 font-bold mr-3">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">{c.name}</h4>
                        <p className="text-xs text-gray-500">{c.date}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 pl-13">{c.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-8">No comments yet. Be the first to comment!</p>
            )}

            <form onSubmit={handleCommentSubmit} className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium text-gray-800 mb-4">Leave a Comment</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name*</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">Comment*</label>
                <textarea
                  id="comment"
                  rows="4"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Post Comment
              </button>
            </form>
          </div>

          {relatedPosts.length > 0 && (
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(r => (
                  <Link key={r.slug} to={`/article/${r.slug}`} className="group block hover:shadow-lg transition-shadow duration-300 rounded-lg overflow-hidden">
                    <div className="h-48 overflow-hidden">
                      <img
                        loading="lazy"
                        src={r.imageUrl}
                        alt={r.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => e.target.src = r.fallbackImage}
                      />
                    </div>
                    <div className="p-4 bg-white">
                      <h4 className="font-bold text-gray-800 group-hover:text-green-600 transition-colors line-clamp-2">{r.title}</h4>
                      <p className="text-sm text-gray-500 mt-1">{r.author}</p>
                      <p className="text-sm text-gray-500">{r.date}</p>
                      {r.excerpt && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{r.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="text-center">
            <Link to="/blog" className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg">
              <FiArrowLeft className="mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleBlog;